<template>
  <b-modal
    ref="modalDocs"
    title="Documentos da Nota Fiscal"
    size="lg"
    hide-footer
    centered
  >
    <p v-if="loadingNotas" role="status"><b-spinner small /> Consultando notas do pedido...</p>
    <b-table
      :items="notas"
      :fields="fields"
      striped
      small
      show-empty
      empty-text="Nenhuma nota fiscal disponível"
      responsive
    >
      <template #cell(acoes)="data">
        <div class="d-flex" style="gap:6px">
          <b-button
            size="sm"
            variant="outline-primary"
            title="Visualizar DANFE (PDF)"
            :disabled="isLoading(data.item)"
            @click="abrir(data.item, 'ver')"
          >
            <b-spinner v-if="isLoading(data.item)" small />
            <feather-icon v-else icon="EyeIcon" size="14" />
            PDF
          </b-button>
          <b-button
            size="sm"
            variant="outline-success"
            title="Baixar DANFE (PDF)"
            :disabled="isLoading(data.item)"
            @click="abrir(data.item, 'pdf')"
          >
            <feather-icon icon="DownloadIcon" size="14" />
            PDF
          </b-button>
          <!-- Download XML oculto temporariamente.
          <b-button
            size="sm"
            variant="outline-secondary"
            title="Baixar arquivo fiscal da nota"
            :disabled="isLoading(data.item)"
            @click="abrir(data.item, 'xml')"
          >
            <feather-icon icon="DownloadIcon" size="14" />
            Arquivo fiscal
          </b-button>
          -->
        </div>
      </template>
    </b-table>
    <p class="text-muted mb-0" style="font-size: 0.85rem">
      Os documentos são buscados diretamente no ERP e podem levar alguns segundos.
    </p>
  </b-modal>
</template>

<script>
import { BModal, BTable, BButton, BSpinner } from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import DocumentoFiscalClient, { baixarBase64, abrirPdfNovaAba } from '@/api/SistemaDKP/documento-fiscal.client';
import PTCNotasClient from '@/api/PTC/PTC_Notas.client';

const client = new DocumentoFiscalClient();
const notasClient = new PTCNotasClient();

export default {
  name: 'ModalDocumentosNF',
  components: {
    BModal, BTable, BButton, BSpinner,
  },
  data() {
    return {
      notas: [],
      loadingNotas: false,
      lookupVersion: 0,
      loadingKeys: {},
      cache: {},
      fields: [
        { key: 'estabelecimentoid', label: 'Estab.' },
        { key: 'notafiscalserie', label: 'Série' },
        { key: 'notafiscalnumero', label: 'Nro NF' },
        { key: 'notafiscaldtemissao', label: 'Emissão' },
        { key: 'acoes', label: 'Documentos' },
      ],
    };
  },
  methods: {
    async show(pedidoId, etapaPedido) {
      const version = ++this.lookupVersion;
      this.notas = [];
      this.cache = {};
      this.loadingKeys = {};
      this.loadingNotas = true;
      this.$refs.modalDocs.show();
      try {
        if (!/^-?[1-9][0-9]*$/.test(String(pedidoId))) throw new Error('Pedido inválido para consultar notas.');
        const response = await notasClient.retornaNotas({
          pedidoid: String(pedidoId), ...(etapaPedido ? { etapaPedido } : {}),
        });
        if (version !== this.lookupVersion) return;
        const notas = response?.notas;
        if (!Array.isArray(notas)) throw new Error('Resposta inválida ao consultar notas do pedido.');
        // Never restore persisted listing metadata on empty results or errors.
        this.notas = notas;
      } catch (err) {
        if (version === this.lookupVersion) {
          this.toast('Erro', err?.response?.data?.message || err?.message || 'Erro ao consultar notas do pedido.', 'danger', 'XCircleIcon');
        }
      } finally {
        if (version === this.lookupVersion) this.loadingNotas = false;
      }
    },
    chave(nf) {
      return `${nf.estabelecimentoid}-${nf.notafiscalserie}-${nf.notafiscalnumero}`;
    },
    isLoading(nf) {
      return !!this.loadingKeys[this.chave(nf)];
    },
    async buscar(nf) {
      const key = this.chave(nf);
      if (this.cache[key]) return this.cache[key];
      const docs = await client.danfeBoleto({
        estabelecimentoid: nf.estabelecimentoid,
        notafiscalserie: nf.notafiscalserie,
        notafiscalnumero: nf.notafiscalnumero,
      });
      this.cache[key] = docs;
      return docs;
    },
    async abrir(nf, modo) {
      if (!nf.estabelecimentoid) {
        this.toast('Atenção', 'Nota fiscal sem estabelecimento — não é possível buscar os documentos.', 'warning', 'AlertTriangleIcon');
        return;
      }
      const key = this.chave(nf);
      this.$set(this.loadingKeys, key, true);
      try {
        const docs = await this.buscar(nf);
        const numero = String(docs?.notaFiscal || nf.notafiscalnumero);
        if (modo === 'xml') {
          if (!docs?.arquivoXML) throw new Error('XML não disponível para esta nota.');
          baixarBase64(docs.arquivoXML, `nf_${numero}.xml`, 'application/xml');
        } else if (modo === 'pdf') {
          if (!docs?.arquivoPDF) throw new Error('PDF não disponível para esta nota.');
          baixarBase64(docs.arquivoPDF, `danfe_${numero}.pdf`, 'application/pdf');
        } else {
          if (!docs?.arquivoPDF) throw new Error('PDF não disponível para esta nota.');
          abrirPdfNovaAba(docs.arquivoPDF);
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Erro ao buscar documentos da nota fiscal.';
        this.toast('Erro', msg, 'danger', 'XCircleIcon');
      } finally {
        this.$set(this.loadingKeys, key, false);
      }
    },
    toast(title, text, variant, icon) {
      this.$toast({
        component: ToastificationContent,
        props: { title, text, icon, variant },
      });
    },
  },
};
</script>
